import { normalizeAnswer } from "@lexireveal/game-engine";
import type { ContentEntry } from "./schema";

/**
 * Contrôles éditoriaux automatisés (EDT-003, CNT-005, CNT-006).
 *
 * Ces contrôles **assistent** le relecteur, ils ne le remplacent jamais : seuls
 * les défauts mécaniquement certains sont des erreurs, le reste est un signalement.
 */

export const DEFINITION_MIN_WORDS = 5;
export const DEFINITION_MAX_WORDS = 16;

/** Longueur minimale d'un mot de définition pris en compte par le test de famille. */
const SIGNIFICANT_WORD_LENGTH = 4;
/** Préfixe commun à partir duquel deux mots sont soupçonnés de même famille. */
const FAMILY_PREFIX_LENGTH = 5;

export type LintCode =
  | "definition_word_count"
  | "possible_word_family"
  | "not_originality_confirmed"
  | "no_reviewer"
  | "ai_drafted";

export type LintWarning = {
  readonly entryId: string;
  readonly code: LintCode;
  readonly message: string;
};

export class ContentValidationError extends Error {
  constructor(
    readonly entryId: string,
    message: string,
  ) {
    super(`${entryId} : ${message}`);
    this.name = "ContentValidationError";
  }
}

export function countWords(definition: string): number {
  return definition.split(/\s+/u).filter((word) => word.length > 0).length;
}

function definitionWords(definition: string): string[] {
  return definition
    .split(/[\s'’-]+/u)
    .map((word) => normalizeAnswer(word))
    .filter((word) => word.length >= SIGNIFICANT_WORD_LENGTH);
}

/**
 * Test de circularité (EDT-003).
 *
 * Erreur : la définition contient la réponse elle-même, sous une forme ou une autre.
 * C'est le seul cas mécaniquement certain.
 */
export function assertNotCircular(entry: ContentEntry): void {
  const answer = normalizeAnswer(entry.answer);
  const definition = normalizeAnswer(entry.definition);

  if (definition.includes(answer)) {
    throw new ContentValidationError(entry.id, "la définition contient la réponse");
  }
}

/** Signale une possible parenté morphologique entre la réponse et un mot de la définition. */
export function findWordFamilyWarnings(entry: ContentEntry): LintWarning[] {
  const answer = normalizeAnswer(entry.answer);
  const warnings: LintWarning[] = [];

  for (const word of new Set(definitionWords(entry.definition))) {
    const sharesPrefix =
      answer.length >= FAMILY_PREFIX_LENGTH &&
      word.length >= FAMILY_PREFIX_LENGTH &&
      answer.slice(0, FAMILY_PREFIX_LENGTH) === word.slice(0, FAMILY_PREFIX_LENGTH);

    // « jour » dans « aujourd'hui » : composant transparent de la réponse.
    const isFragmentOfAnswer = answer.includes(word);

    if (sharesPrefix || isFragmentOfAnswer) {
      warnings.push({
        entryId: entry.id,
        code: "possible_word_family",
        message: `« ${word} » est probablement de la même famille que la réponse — à relire`,
      });
    }
  }

  return warnings;
}

export function lintEntry(entry: ContentEntry): LintWarning[] {
  const warnings: LintWarning[] = [...findWordFamilyWarnings(entry)];
  const words = countWords(entry.definition);

  if (words < DEFINITION_MIN_WORDS || words > DEFINITION_MAX_WORDS) {
    warnings.push({
      entryId: entry.id,
      code: "definition_word_count",
      message: `définition de ${words} mots, hors de la fourchette ${DEFINITION_MIN_WORDS}–${DEFINITION_MAX_WORDS}`,
    });
  }

  if (!entry.originalityConfirmed) {
    warnings.push({
      entryId: entry.id,
      code: "not_originality_confirmed",
      message: "originalité non confirmée par un humain",
    });
  }

  if (entry.reviewedBy === null) {
    warnings.push({
      entryId: entry.id,
      code: "no_reviewer",
      message: "aucune seconde relecture",
    });
  }

  if (entry.draftedBy === "ai_assisted") {
    warnings.push({
      entryId: entry.id,
      code: "ai_drafted",
      message: "rédigée avec assistance IA : validation humaine obligatoire avant publication",
    });
  }

  return warnings;
}
